import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { testimonials } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('提交成功，我们会尽快与你联系！');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9q9s8ee3oq4jqumtiqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！我们会尽快与你联系。');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || text || '提交失败，请稍后再试';
        if (msg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormMessage(msg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  const aboutBgUrl = 'https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20diverse%20young%20team%20collaborating%2C%20colorful%20art%20on%20walls%2C%20warm%20lighting%2C%20plants%2C%20inspirational%20atmosphere%2C%20casual%20creative%20workspace&width=1800&height=600&seq=about-cy-hero&orientation=landscape';

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Hero */}
        <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <img
            src={aboutBgUrl}
            alt="关于cy社区"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                关于cy社区
              </h1>
              <p className="text-sm md:text-base text-background-50/80 max-w-xl">
                汇聚创意青年，发现无限可能
              </p>
            </div>
          </div>
        </section>

        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-14">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
              品牌介绍
            </h2>
            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                <strong>cy社区</strong>成立于2024年，是一个专为创意青年打造的互动交流平台。我们相信每个年轻人心中都有一团创意的火焰，而cy社区的使命就是为这团火焰提供燃烧的空间。
              </p>
              <p>
                在这里，你可以<strong>分享你的创意作品</strong>——无论是插画、摄影、文字、音乐还是手工艺品，都能找到欣赏你的观众。你可以<strong>参与热门话题讨论</strong>，与志同道合的伙伴碰撞思想的火花。你还可以<strong>发现灵感</strong>，从其他创作者的优秀作品中汲取养分。
              </p>
              <p>
                截至目前，cy社区已经汇聚了<strong>超过12,800名注册创作者</strong>，累计发布了<strong>58,000多件创意作品</strong>，日均互动量超过<strong>3.2万次</strong>。我们的社区涵盖了视觉设计、摄影摄像、文字创作、音乐音频、科技创意、手工制作、生活方式等七大创意领域。
              </p>
              <p>
                cy社区的核心理念是<strong>开放、包容、共创</strong>。我们相信每个人都有独特的创意表达，都值得被看见、被欣赏。无论你是专业创作者还是业余爱好者，这里都有属于你的舞台。
              </p>
            </div>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
              我们的愿景
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-lightbulb-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">激发创意</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  为每一个创意提供展示的舞台，让灵感在交流中迸发，让创意在碰撞中成长。
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-group-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">连接同好</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  打破地理和领域的界限，让志同道合的创意青年相遇、相识、相知，共同成长。
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-star-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">赋能成长</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  通过教程、资源和社区支持，帮助每一位创作者不断进步，从兴趣走向专业。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="w-full px-4 md:px-6 py-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2 text-center">
              社区口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">来自社区成员的真实评价</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((item) => (
                <div key={item.name} className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{item.name}</div>
                      <div className="text-xs text-foreground-500">{item.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              有任何问题或合作意向，欢迎随时联系我们
            </p>

            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
              <form onSubmit={handleContactSubmit} data-readdy-form className="space-y-5">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-hp"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:border-primary-300 focus:outline-none focus:ring-1 focus:ring-primary-200 transition-colors"
                  >
                    <option value="">请选择主题</option>
                    <option value="合作">商务合作</option>
                    <option value="反馈">意见反馈</option>
                    <option value="媒体">媒体报道</option>
                    <option value="其他">其他问题</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入你的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
              </form>

              {formStatus !== 'idle' && (
                <div
                  className={`mt-4 p-3 rounded-lg text-sm text-center ${
                    formStatus === 'success'
                      ? 'bg-accent-100 text-accent-800'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {formMessage}
                </div>
              )}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h4>
                <p className="text-xs text-foreground-500">hello@cycommunity.com</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-wechat-line text-accent-600"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信</h4>
                <p className="text-xs text-foreground-500">cycommunity_official</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-map-pin-line text-secondary-600"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">地址</h4>
                <p className="text-xs text-foreground-500">北京市朝阳区创意产业园A座</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 md:px-6 py-16 bg-primary-500">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              加入cy社区，开启你的创意之旅
            </h2>
            <p className="text-sm text-background-50/80 mb-8">
              与12,800+创意青年一起，分享作品、交流灵感、共同成长
            </p>
            <Link
              to="/"
              className="inline-block px-8 py-3.5 rounded-full bg-background-50 text-primary-600 font-medium text-sm hover:bg-background-100 transition-all duration-200 whitespace-nowrap no-underline"
            >
              立即加入
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于cy社区 - 创意青年聚集地',
            description:
              'cy社区成立于2024年，是一个专为创意青年打造的互动交流平台，汇聚超过12,800名创作者，涵盖设计、摄影、写作、音乐等七大创意领域。',
            url: 'https://cycommunity.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: 'cy社区',
              url: 'https://cycommunity.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://cycommunity.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
    </>
  );
}